/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import React, { useEffect } from "react";
import {
  AbstractWidgetProps,
  StagePanelLocation,
  StagePanelSection,
  UiItemsProvider,
  WidgetState,
} from "@itwin/appui-abstract";
import {
  Alert,
  // Button,
  // LabeledSelect,
  ToggleSwitch,
} from "@itwin/itwinui-react";
import {
  DisplayStyle3dSettingsProps,
  RenderMode,
  SkyBoxProps,
  ViewFlagProps,
} from "@itwin/core-common";
import { DisplayStylesApp } from "../DisplaySettings/DisplayStyles";
import {
  useActiveIModelConnection,
  useActiveViewport,
} from "@itwin/appui-react";

export const RenderWidget = () => {
  const iModelConnection = useActiveIModelConnection();
  const viewport = useActiveViewport();
  const [showShaders, setShowShaders] = React.useState<boolean>(true);
  // const [clipPlaneState, setClipPlaneState] = React.useState<string>("None");

  const defaultSkyBox: SkyBoxProps = {
    display: true,
    twoColor: true,
    groundColor: 9741199,
    nadirColor: 5464143,
    skyColor: 16764303,
    zenithColor: 16741686,
  };

  const renderingStyleViewFlagsHigh: ViewFlagProps = {
    noConstruct: false,
    noCameraLights: false,
    noSourceLights: false,
    noSolarLight: true,
    visEdges: false,
    hidEdges: false,
    shadows: true,
    grid: false,
    monochrome: false,
    ambientOcclusion: true,
    thematicDisplay: false,
    renderMode: RenderMode.SmoothShade,
  };

  let displayStylesHigh: DisplayStyle3dSettingsProps = {
    environment: {
      sky: defaultSkyBox,
    },
    viewflags: renderingStyleViewFlagsHigh,
    backgroundMap: {
      applyTerrain: true,
      groundBias: 0,
      terrainSettings: { applyLighting: true },
    },
    solarShadows: { color: 0x524f44 },
    lights: {
      solar: {
        direction: [
          0.9391245716329828, 0.10165764029437066, -0.3281931795832247,
        ],
      },
      hemisphere: { intensity: 0.2 },
      portrait: { intensity: 0 },
    },
  };
  const renderingStyleViewFlagsLow: ViewFlagProps = {
    noConstruct: false,
    noCameraLights: false,
    noSourceLights: false,
    noSolarLight: true,
    visEdges: false,
    hidEdges: false,
    shadows: false,
    grid: false,
    monochrome: false,
    ambientOcclusion: false,
    thematicDisplay: false,
    renderMode: RenderMode.SolidFill,
  };

  let displayStylesLow: DisplayStyle3dSettingsProps = {
    environment: {
      sky: defaultSkyBox,
    },
    viewflags: renderingStyleViewFlagsLow,
    backgroundMap: {
      applyTerrain: true,
      groundBias: 0,
      terrainSettings: { applyLighting: true },
    },
    lights: {
      solar: {
        direction: [
          0.9391245716329828, 0.10165764029437066, -0.3281931795832247,
        ],
      },
      hemisphere: { intensity: 0.2 },
      portrait: { intensity: 0 },
    },
  };

  useEffect(() => {
    if (undefined === viewport || undefined === iModelConnection) {
      return;
    }

    if (viewport && showShaders)
      DisplayStylesApp.applyDisplayStyle(viewport, displayStylesHigh);
    if (viewport && !showShaders)
      DisplayStylesApp.applyDisplayStyle(viewport, displayStylesLow);
    // eslint-disable-next-line
  }, [iModelConnection, viewport, showShaders]);

  /* Turn on/off the clip range */
  const _onToggleRangeClip = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowShaders(e.target.checked);
    //    setClipPlaneState("None");
  };

  return (
    <div className="sample-options">
      <div className="sample-grid">
        <ToggleSwitch
          label="Use shaders"
          checked={showShaders}
          onChange={_onToggleRangeClip}
        />
      </div>
      <Alert type="informational" className="instructions">
        If you experience performance issues, you can turn off screen-space
        shaders here.
      </Alert>
    </div>
  );
};

export class RenderWidgetProvider implements UiItemsProvider {
  public readonly id: string = "RenderWidgetProvider";

  public provideWidgets(
    _stageId: string,
    _stageUsage: string,
    location: StagePanelLocation,
    _section?: StagePanelSection
  ): ReadonlyArray<AbstractWidgetProps> {
    const widgets: AbstractWidgetProps[] = [];
    if (location === StagePanelLocation.Bottom) {
      widgets.push({
        id: "RenderWidget",
        label: "Render Settings",
        defaultState: WidgetState.Open,
        getWidgetContent: () => <RenderWidget />,
      });
    }
    return widgets;
  }
}
